<script setup lang="ts">
import { computed, ref } from 'vue';
import PickupMap from './PickupMap.vue';
import {
  formatAvailableFrom,
  formatDayLabel,
  formatDistance,
  formatEuro,
  googleMapsUrl,
  openingSummary,
  type DeliveryOptionsView,
} from '../lib/deliveryOptions';

const props = defineProps<{ view: DeliveryOptionsView }>();

const NEAREST_SHOWN = 3;

const showAllPickups = ref(false);
const selected = ref<number | null>(null);
const expandedHours = ref<number | null>(null);

const visiblePickups = computed(() =>
  showAllPickups.value ? props.view.pickups : props.view.pickups.slice(0, NEAREST_SHOWN),
);

const hiddenCount = computed(() => Math.max(0, props.view.pickups.length - NEAREST_SHOWN));

/** The moment is identical for every location in practice, so it belongs in the header. */
const pickupFrom = computed(() => formatAvailableFrom(props.view.pickups[0]?.availableFrom ?? null));

function select(index: number) {
  selected.value = selected.value === index ? null : index;
  // Selecting a location that is out of view is confusing, so open the full list.
  if (index >= NEAREST_SHOWN) {
    showAllPickups.value = true;
  }
}
</script>

<template>
  <div class="opts">
    <p v-if="view.address" class="opts__address">Bezorgopties voor {{ view.address }}</p>

    <p v-if="view.carrierWarning" class="opts__warn">{{ view.carrierWarning }}</p>

    <section v-if="view.days.length > 0" class="opts__section">
      <h4 class="opts__heading">Thuisbezorging</h4>

      <ul class="days">
        <li v-for="day in view.days" :key="day.date" class="day">
          <span class="day__label">{{ formatDayLabel(day.date) }}</span>
          <span class="day__slots">
            <span v-for="slot in day.slots" :key="`${slot.typeId}-${slot.start}`" class="slot">
              <span class="slot__time">{{ slot.start }}–{{ slot.end }}</span>
              <span class="slot__type">{{ slot.label }}</span>
              <span class="slot__price" :class="{ 'slot__price--free': slot.cents === 0 }">
                {{ slot.cents === 0 ? 'gratis' : `+ ${formatEuro(slot.cents)}` }}
              </span>
            </span>
            <span v-if="day.slots.length === 0" class="day__none">geen opties</span>
          </span>
        </li>
      </ul>

      <p v-if="view.unavailableTypes.length > 0" class="opts__note">
        Niet beschikbaar op dit adres: {{ view.unavailableTypes.join(', ').toLowerCase() }}.
      </p>
    </section>

    <section v-if="view.pickups.length > 0" class="opts__section">
      <h4 class="opts__heading">
        Ophaalpunten
        <span class="opts__count">{{ view.pickups.length }}</span>
      </h4>
      <p v-if="pickupFrom" class="opts__note">Op te halen {{ pickupFrom }}.</p>

      <PickupMap :points="view.pickups" :focus="visiblePickups" :selected="selected" @select="select" />

      <ul class="points">
        <li
          v-for="(point, index) in visiblePickups"
          :key="`${point.name}-${index}`"
          class="point"
          :class="{ 'point--selected': selected === index }"
        >
          <button class="point__main" type="button" @click="select(index)">
            <span class="point__name">{{ point.name }}</span>
            <span class="point__meta">
              {{ point.address }}, {{ point.city }} · {{ formatDistance(point.distanceMetres) }}
            </span>
            <span class="point__hours">
              <template v-if="openingSummary(point)">{{ openingSummary(point) }}</template>
              <template v-else>Wisselende openingstijden</template>
              <template v-if="point.priceCents !== null"> · + {{ formatEuro(point.priceCents) }}</template>
            </span>
          </button>

          <div class="point__actions">
            <button
              v-if="!openingSummary(point)"
              class="point__link"
              type="button"
              @click="expandedHours = expandedHours === index ? null : index"
            >
              {{ expandedHours === index ? 'Tijden verbergen' : 'Openingstijden' }}
            </button>
            <a class="point__link" :href="googleMapsUrl(point)" target="_blank" rel="noopener noreferrer">
              Route
            </a>
          </div>

          <table v-if="expandedHours === index" class="hours">
            <tbody>
              <tr v-for="day in point.openingHours" :key="day.label">
                <th scope="row">{{ day.label }}</th>
                <td v-if="day.ranges.length === 0" class="hours__closed">gesloten</td>
                <td v-else>
                  {{ day.ranges.map((range) => `${range.start}–${range.end}`).join(', ') }}
                </td>
              </tr>
            </tbody>
          </table>
        </li>
      </ul>

      <button v-if="hiddenCount > 0" class="opts__more" type="button" @click="showAllPickups = !showAllPickups">
        {{ showAllPickups ? 'Toon alleen de dichtstbijzijnde' : `Toon ${hiddenCount} andere ophaalpunten` }}
      </button>
    </section>

    <p v-if="view.problems" class="opts__warn">
      Een deel van het antwoord had niet de verwachte vorm. Open de ruwe respons hieronder.
    </p>
  </div>
</template>

<style scoped>
.opts {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 12px;
  border-top: 1px solid var(--border);
}

.opts__address {
  margin: 0;
  font-size: 13px;
  color: var(--text-muted);
}

.opts__section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.opts__heading {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
}

.opts__count {
  font-weight: 400;
  font-size: 12px;
  color: var(--text-muted);
  background: var(--surface-muted);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0 6px;
}

.opts__note,
.opts__warn {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
}

.opts__warn {
  color: var(--warn-accent);
  background: var(--warn-bg);
  border: 1px solid var(--warn-border);
  border-radius: 8px;
  padding: 6px 8px;
}

.days {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.day {
  display: flex;
  gap: 10px;
  align-items: baseline;
  padding: 5px 0;
  border-bottom: 1px solid var(--border);
}

.day:last-child {
  border-bottom: none;
}

.day__label {
  flex: 0 0 68px;
  font-size: 13px;
  font-weight: 500;
}

.day__slots {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  flex: 1;
}

.day__none {
  font-size: 12px;
  color: var(--text-muted);
}

.slot {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 3px 9px;
}

.slot__time {
  font-variant-numeric: tabular-nums;
}

.slot__type {
  color: var(--text-muted);
}

.slot__price {
  font-weight: 500;
}

.slot__price--free {
  color: var(--accent);
}

.points {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.point {
  border: 1px solid var(--border);
  border-radius: 9px;
  background: var(--surface);
  padding: 8px 10px;
}

.point--selected {
  border-color: var(--accent);
  background: var(--accent-soft);
}

.point__main {
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--text);
  text-align: left;
  cursor: pointer;
}

.point__name {
  font-size: 13px;
  font-weight: 500;
}

.point__meta,
.point__hours {
  font-size: 12px;
  color: var(--text-muted);
}

.point__actions {
  display: flex;
  gap: 12px;
  margin-top: 6px;
}

.point__link {
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 12px;
  color: var(--accent);
  cursor: pointer;
  text-decoration: underline;
}

.hours {
  margin-top: 8px;
  border-collapse: collapse;
  font-size: 12px;
}

.hours th {
  text-align: left;
  font-weight: 400;
  color: var(--text-muted);
  padding-right: 12px;
}

.hours td {
  font-variant-numeric: tabular-nums;
}

.hours__closed {
  color: var(--text-muted);
}

.opts__more {
  align-self: flex-start;
  background: none;
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 5px 10px;
  font: inherit;
  font-size: 12px;
  color: var(--text);
  cursor: pointer;
}

.opts__more:hover {
  border-color: var(--accent);
}
</style>
